import React from 'react';
import { Eye, ThumbsUp, MessageCircle, Share2, MousePointerClick } from 'lucide-react';
import TendTooltip from '../common/TendTooltip';
import { CHANNEL_INFO } from '../../utils/editorialCadence';
import { formatCompactNumber } from '../../utils/analyticsFormatters';
import { LINK_CLICKS_TOOLTIP, nrTooltip, ownCommentTooltip, platformMetricTooltip, TooltipContent } from '../../utils/metricAnnotations';
import type { PublishedPostRow } from '../../api/analyticsApi';

const FRESH_MS = 60 * 60 * 1000;

interface TendCardMetricsRowProps {
  /** Latest-publish metrics for this item (absent = published seconds ago
   * or the bulk posts fetch hasn't caught up — render pending dashes). */
  metricsRow?: PublishedPostRow;
  /** Brand-attributed publish time, for the pending-tooltip freshness rule. */
  publishedAt?: string | null;
  /** Matches the header chrome's queued wash. */
  queued?: boolean;
}

/**
 * Sub-header metrics tab: the published post's lifetime impressions /
 * likes / comments / shares / clicks, hanging out of the tended card's
 * header chrome as a centered strip (square top, rounded bottom — the
 * original card's ContentAnalyticsBar recipe). Icon-labeled with the full
 * metric name in the tooltip, compact numbers (1.1M / 10K / 345).
 *
 * Clicks are the FIRST-PARTY Tend short-link count (the Original card and
 * the channel table's definition), never the platform-reported figure —
 * most platforms don't report clicks at all, which left this cell reading
 * NR everywhere.
 */
const TendCardMetricsRow: React.FC<TendCardMetricsRowProps> = ({
  metricsRow,
  publishedAt,
  queued,
}) => {
  const metrics = metricsRow?.metrics;
  const pending = !metrics?.metrics_available;
  const effectivePublishedAt = publishedAt || metricsRow?.published_at;
  const isFresh = effectivePublishedAt
    && (Date.now() - new Date(effectivePublishedAt).getTime()) < FRESH_MS;
  const pendingTitle = isFresh
    ? 'Metrics pending — first snapshot lands within the hour'
    : 'No metrics recorded for this post';

  const cell = (label: string, icon: React.ReactNode, value: number | null | undefined, metricKey: string, annotation?: TooltipContent) => {
    // null = the platform never reported this metric ("NR"), not a zero
    const tooltip = pending
      ? { title: label, subtext: `${pendingTitle}.` }
      : value === null
        ? nrTooltip(metricKey, metricsRow?.platform, metricsRow ? CHANNEL_INFO[metricsRow.platform]?.name : undefined)
        : annotation;
    return (
      <TendTooltip key={label} content={tooltip} title={tooltip ? undefined : label}>
        <div className="tend-card-metric" aria-label={label}>
          {icon}
          <span className={`tend-card-metric-num ${pending || value === null ? 'is-pending' : ''}`}>
            {pending ? '—' : value === null ? 'NR' : formatCompactNumber(value ?? 0)}
          </span>
        </div>
      </TendTooltip>
    );
  };

  const ownExcluded = metrics && metrics.own_comments_excluded > 0
    ? ownCommentTooltip(metrics.own_comments_excluded)
    : undefined;

  return (
    <div className="tend-card-metrics-row">
      <div className={`tend-card-metrics ${queued ? 'is-queued' : ''}`}>
        {cell('Impressions', <Eye className="w-4 h-4" />, metrics?.impressions, 'impressions', platformMetricTooltip('impressions', metricsRow?.platform))}
        {cell('Likes', <ThumbsUp className="w-4 h-4" />, metrics?.likes, 'likes', platformMetricTooltip('likes', metricsRow?.platform))}
        {cell('Comments', <MessageCircle className="w-4 h-4" />, metrics?.comments, 'comments', ownExcluded ?? platformMetricTooltip('comments', metricsRow?.platform))}
        {cell('Shares', <Share2 className="w-4 h-4" />, metrics?.shares, 'shares', platformMetricTooltip('shares', metricsRow?.platform))}
        <div className="tend-card-metric-div" aria-hidden="true" />
        {/* Link clicks are always tracked by us, so they render as soon
            as the post row exists — even while platform metrics pend.
            Pinterest shows the outbound clicks it reports instead (its
            pins carry no TEND link), pending/NR like any platform metric. */}
        {metrics?.clicks_source === 'none' ? (
          // Posts on this network carry no link: NR with the reason
          cell('Clicks', <MousePointerClick className="w-4 h-4" />, null, 'clicks')
        ) : metrics?.clicks_source === 'network' ? (
          cell('Clicks', <MousePointerClick className="w-4 h-4" />, metrics.clicks, 'clicks', platformMetricTooltip('clicks', metricsRow?.platform))
        ) : (
          <TendTooltip content={metricsRow ? LINK_CLICKS_TOOLTIP : undefined} title={metricsRow ? undefined : 'Link clicks'}>
            <div className="tend-card-metric" aria-label="Link clicks">
              <MousePointerClick className="w-4 h-4" />
              <span className={`tend-card-metric-num ${metricsRow ? '' : 'is-pending'}`}>
                {metricsRow ? formatCompactNumber(metrics?.link_clicks ?? 0) : '—'}
              </span>
            </div>
          </TendTooltip>
        )}
      </div>
    </div>
  );
};

export default TendCardMetricsRow;
